import React from "react"
import { Box, Flex, Image, Input, PseudoBox, Text } from "@chakra-ui/core"
import { bp } from "../../utils/MediaQueries"
import { submitCatalogDownloadEmail } from "../../api/catalogDownloadEmail"

// Direct-download link for the 2026 catalog PDF (served as a static asset).
export const CATALOG_PDF_URL = "/catalog-2026.pdf"

const DownloadIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
    <path
      d="M9 1.5v9m0 0L5.5 7M9 10.5 12.5 7"
      stroke="white"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M2.5 12.5v1.5a2 2 0 0 0 2 2h9a2 2 0 0 0 2-2v-1.5"
      stroke="white"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
  </svg>
)

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const CatalogDownloadWidget = () => {
  const [email, setEmail] = React.useState("")
  const [isSubmitting, setIsSubmitting] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!EMAIL_REGEX.test(email)) {
      setError("Please enter a valid email address.")
      return
    }
    setError(null)
    setIsSubmitting(true)
    try {
      await submitCatalogDownloadEmail(email)
      const link = document.createElement("a")
      link.href = CATALOG_PDF_URL
      link.download = "sixthreezero-catalog-2026.pdf"
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } catch (e) {
      setError("Something went wrong. Please try again.")
    }
    setIsSubmitting(false)
  }

  return (
    <Box as="section" bg="night" w="100%" py={bp("3rem", "5rem")}>
      <Flex
        maxW="1180px"
        mx="auto"
        px={bp("1.25rem", "2rem")}
        direction={bp("column", "row")}
        align="center"
        justify="center"
        gridGap={bp("2rem", "4.5rem")}
      >
        {/* Catalog cover */}
        <Box
          flexShrink={0}
          w={bp("80%", "440px")}
          maxW="440px"
          transform={bp("none", "perspective(1400px) rotateY(7deg)")}
          transformOrigin="right center"
        >
          <Image
            src="/images/catalog-cover.jpg"
            alt="sixthreezero 2026 Electric Bikes & Electric Trikes Catalog"
            w="100%"
            borderRadius="6px"
            boxShadow="0 30px 60px rgba(0, 0, 0, 0.55)"
          />
        </Box>

        {/* Copy + download */}
        <Box maxW={bp("100%", "440px")} textAlign={bp("center", "left")}>
          <Text
            color="primary"
            fontSize={bp("11px", "12px")}
            fontWeight="700"
            letterSpacing="2px"
            mb={bp("12px", "16px")}
          >
            FREE 2026 CATALOG · PDF
          </Text>

          <Text
            as="h2"
            color="white"
            fontSize={bp("28px", "44px")}
            fontWeight="700"
            lineHeight="1.1"
            letterSpacing="-0.5px"
          >
            Browse the whole lineup, in one place.
          </Text>

          <Text
            color="rgba(255, 255, 255, 0.7)"
            fontSize={bp("14px", "16px")}
            lineHeight="1.6"
            mt={bp("14px", "20px")}
          >
            Every bike, e-bike, and trike — specs, colors, fit, and pricing — in
            one beautifully laid-out PDF. Download it instantly, free.
          </Text>

          <Box as="form" onSubmit={handleSubmit} mt={bp("22px", "30px")}>
            <Flex
              direction={bp("column", "row")}
              justify={bp("center", "flex-start")}
              align="stretch"
              gridGap="12px"
            >
              <Input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setEmail(e.target.value)
                }
                isRequired
                bg="white"
                borderRadius="8px"
                h={bp("48px", "52px")}
                w={bp("100%", "260px")}
              />
              <PseudoBox
                as="button"
                // @ts-ignore — Chakra PseudoBox passes button attrs through
                type="submit"
                disabled={isSubmitting}
                display="inline-flex"
                alignItems="center"
                justifyContent="center"
                bg="primary"
                color="white"
                fontSize={bp("15px", "16px")}
                fontWeight="700"
                px={bp("24px", "32px")}
                h={bp("48px", "52px")}
                borderRadius="8px"
                cursor="pointer"
                whiteSpace="nowrap"
                flexShrink={0}
                opacity={isSubmitting ? 0.7 : 1}
                transition="background 0.2s ease, transform 0.2s ease"
                _hover={{ bg: "primary200", transform: "translateY(-1px)" }}
              >
                <Box as="span" mr="8px" flexShrink={0}>
                  <DownloadIcon />
                </Box>
                {isSubmitting ? "Sending..." : "Get the Catalog"}
              </PseudoBox>
            </Flex>
          </Box>

          {error && (
            <Text color="red.400" fontSize="13px" mt="10px">
              {error}
            </Text>
          )}

          <Text
            color="rgba(255, 255, 255, 0.45)"
            fontSize={bp("11px", "12px")}
            mt={bp("12px", "14px")}
          >
            Enter your email and the catalog downloads instantly.
          </Text>
        </Box>
      </Flex>
    </Box>
  )
}

export default CatalogDownloadWidget
